import { JetView } from 'webix-jet'; // Importing JetView from webix-jet framework
import { allpayments } from 'models/allpayments'; // Importing allpayments model
import { devConfig, profile } from 'config/config'; // Importing configuration settings

/**
 * Class representing the table view for balance history.
 * @extends JetView
 */
export default class tableBalanceHistorysView extends JetView {
    /**
     * Configures the view layout and components.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const dateFormat = webix.Date.dateToStr(this.app.config.dateFormat); // Date formatting function

        /**
         * Marks old values in the BalanceGap column based on specified conditions.
         * @param {number} value - The value to evaluate.
         * @param {Object} config - The configuration object.
         * @returns {Object} CSS styles for the cell.
         */
        function mark_old(value, config) {
            var num = webix.Number.parse(value, {
                decimalSize: 2,
                groupSize: 3,
                decimalDelimiter: '.',
                groupDelimiter: ',',
            });
            if (num > 30 && num < 99) {
                return { background: 'orange' }; // Orange background for values between 30 and 99
            }
            if (num > 100) {
                return { background: 'red' }; // Red background for values greater than 100
            }
        }

        return {
            rows: [
                {
                    cols: [
                        {
                            view: 'treetable', // Treetable view for displaying hierarchical data
                            id: 'datatable', // Unique identifier for the datatable
                            css: 'webix_header_border webix_data_border', // CSS classes for styling
                            scheme: {
                                $change: function (item) {
                                    // Change event for item updates
                                    if (item.BalanceEnd != item.BalanceEndCalc) {
                                        item.$css = { background: '#FFAAAA' }; // Highlight changed items
                                        if (item.BalanceEnd > item.BalanceEndCalc) {
                                            item.BalanceGap = item.BalanceEnd - item.BalanceEndCalc; // Calculate balance gap
                                        } else {
                                            item.BalanceGap = item.BalanceEndCalc - item.BalanceEnd; // Calculate balance gap
                                        }
                                    }
                                },
                            },
                            columns: [
                                {
                                    id: 'Partner', // Column for partner information
                                    header: _('Partner'), // Localized header
                                    width: 250, // Column width
                                    sort: 'int', // Sorting type
                                    template: function (obj, common) {
                                        // Template for rendering partner names
                                        if (obj.$group)
                                            return (
                                                common.treetable(obj, common) +
                                                "<span class='partner " +
                                                obj.Partner +
                                                "'></span>"
                                            );
                                        return "<span class='partner " + obj.Partner + "'></span>";
                                    },
                                },
                                {
                                    id: 'CreatedLocalDate', // Column for creation date
                                    header: _('Fecha de creación'), // Localized header
                                    fillspace: 0,
                                    minWidth: 0,
                                    width: 120,
                                    sort: 'text', // Sorting type
                                    format: dateFormat, // Date formatting
                                    footer: 'Total:', // Footer text
                                },
                                {
                                    id: 'Country', // Column for country information
                                    header: _('País'), // Localized header
                                    hidden: !devConfig.getPermAction('sensitiveInformationCountry', 'Add'), // Conditional visibility based on permissions
                                    css: 'prm-filterByCountry', // CSS class for styling
                                    width: 80,
                                    sort: 'int', // Sorting type
                                    template: function (obj) {
                                        // Template for rendering country flags
                                        return "<i class='flags " + obj.Country + "'></i>";
                                    },
                                },
                                {
                                    id: 'UserId', // Column for user ID
                                    header: _('ID Usuario'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                },
                                {
                                    id: 'UserName', // Column for user name
                                    header: _('Nombre Usuario'), // Localized header
                                    width: 250,
                                    sort: 'int', // Sorting type
                                    hidden: !devConfig.getPermAction('sensitiveInformationNames', 'Add'), // Conditional visibility based on permissions
                                },
                                {
                                    id: 'BalanceInitial', // Column for initial balance
                                    header: _('Saldo Inicial'), // Localized header
                                    width: 130,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                },
                                {
                                    id: 'AmountDeposits', // Column for deposit amounts
                                    header: _('Depósitos'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'AmountBets', // Column for betting amounts
                                    header: _('Ap. Deportivas'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'retentionOnBets', // Column for retention on bets
                                    header: _('Impuesto Apuesta'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'AmountWin', // Column for winning amounts
                                    header: _('Pr. Deportivas'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'GgrAmountWin', // Column for GGR winning amounts
                                    header: _('GGR. Deportivas'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'retentionsPrizesSports', // Column for sports prize retentions
                                    header: _('Retenciones premios deportivas'), // Localized header
                                    width: 180,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'float', // Export type
                                },
                                {
                                    id: 'AmountBetsCasino', // Column for casino betting amounts
                                    header: _('Ap. Casino'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'retentionOnBetsCasino', // Column for casino bet retentions
                                    header: _('Impuesto Apuesta Casino'), // Localized header
                                    width: 120,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'AmountWinCasino', // Column for casino winning amounts
                                    header: _('Pr. Casino'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'GgrAmountWinCasino', // Column for GGR casino winning amounts
                                    header: _('GGR. Casino'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'retentionOnBets', // Column for bet retentions
                                    header: _('Retenciones a las apuestas'), // Localized header
                                    width: 130,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'float', // Export type
                                },
                                {
                                    id: 'WithdrawCreates', // Column for created withdrawals
                                    header: _('Retiros Creados'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'WithdrawDeletes', // Column for deleted withdrawals
                                    header: _('Retiros Eliminados'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'AdjustmentE', // Column for adjustments E
                                    header: _('Ajustes E'), // Localized header
                                    width: 80,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'AdjustmentS', // Column for adjustments S
                                    header: _('Ajustes S'), // Localized header
                                    width: 80,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'Bonus', // Column for bonuses
                                    header: _('Bonos'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'BonusCasino', // Column for casino bonuses
                                    header: _('Bonos casino'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'BonusVirtual', // Column for virtual bonuses
                                    header: _('Bonos Virtuales'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'BonusCasinoLive', // Column for live casino bonuses
                                    header: _('Bonos Casino en vivo'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'AwardsJackpotCasino', // Column for casino jackpot awards
                                    header: _('Premios Jackpot Casino'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'float', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'AwardsJackpotSport', // Column for sports jackpot awards
                                    header: _('Premios Jackpot deportivas'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'float', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'BalanceEnd', // Column for final balance
                                    header: _('Saldo Final'), // Localized header
                                    width: 130,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'BalanceEndCalc', // Column for calculated final balance
                                    header: _('Saldo Final Calc.'), // Localized header
                                    width: 130,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'BalanceGap', // Column for balance gap
                                    header: _('Calc. Desfase'), // Localized header
                                    width: 110,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    cssFormat: mark_old, // Custom CSS formatting function
                                },
                                {
                                    id: 'WithdrawPaid', // Column for paid withdrawals
                                    header: _('Retiros Pagados'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'WithdrawPend', // Column for pending withdrawals
                                    header: _('Retiros Pendientes'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'BonusFreeWin', // Column for free win bonuses
                                    header: _('Bonos Free Ganados'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'retentionsDeposit', // Column for deposit retentions
                                    header: _('Impuesto Deposito'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'DepositsUsersCount', // Column for count of depositing users
                                    header: _('Usuarios Depositantes'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'DepositsCount', // Column for count of deposits
                                    header: _('Cantidad Depositos'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'StakesLotteries', // Column for lottery sales
                                    header: _('Venta Loterías'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'AwardsLotteries', // Column for lottery rollbacks
                                    header: _('Rollbacks de lotería'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'AmountWinGamification', // Column for lottery sales
                                    header: _('Entradas Gamificación'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                                {
                                    id: 'AmountBetsGamification', // Column for lottery rollbacks
                                    header: _('Salidas Gamificación'), // Localized header
                                    width: 100,
                                    sort: 'int', // Sorting type
                                    format: webix.i18n.priceFormat, // Price formatting
                                    exportType: 'number', // Export type
                                    footer: { content: 'summColumn' }, // Footer content
                                },
                            ],
                            on: {
                                onBeforeLoad: function () {
                                    // Event before loading data
                                    this.showOverlay(
                                        "<div id='contenedor'; class='container-preload'><div style='position: fixed; top: calc(50% - 57px);left: calc(50% - 60px);width: 120px;' class='progress'><div class='icon-preload girando'></div><div class='load-text'>Loading... </div></div></div>",
                                    ); // Show loading overlay
                                },
                                onAfterLoad: function () {
                                    // Event after loading data
                                    this.hideOverlay(); // Hide loading overlay
                                },
                            },
                            ready: function () {
                                // Event when the datatable is ready
                                if (!this.count()) {
                                    webix.extend(this, webix.OverlayBox); // Extend with overlay functionality
                                    this.showOverlay(
                                        "<div class='no-data' style='margin:75px; font-size:18px;'>" +
                                            _('No hay datos') +
                                            '</div>', // Show no data message
                                    );
                                }
                            },
                            footer: true, // Enable footer
                            tooltip: true, // Enable tooltips
                            math: true, // Enable math calculations
                            loadahead: 0, // Load ahead configuration
                            datafetch: devConfig.getDefaultDataFetch(), // Default data fetch size
                            pager: 'pagerA', // Pager ID for pagination
                        },
                    ],
                },
            ],
        };
    }

    /**
     * Initializes the view and sets up event handlers.
     * @param {Object} grid - The datatable instance.
     */
    init(grid) {
        grid = $$('datatable'); // Get the datatable instance
        var url = devConfig.geturlApi() + 'es/BalanceHistory/GetBalanceHistory'; // API URL for fetching balance history
        var filter = ''; // Filter string for API requests
        const _ = this.app.getService('locale')._; // Localization service
        var flag = false; // Flag for export operations

        grid.attachEvent('onAfterLoad', function () {
            // Event after data is loaded
            $$('datatable').data.group({
                by: 'Partner', // Group data by Partner
                map: {
                    Partner: ['Partner'], // Mapping for grouping
                },
            });
        });

        this.on(this.app, 'balanceHistory:filter', (filterData) => {
            // Event for applying filters
            var object = Object.entries(filterData); // Convert filter data to entries
            var string1 = ''; // Initialize filter string
            for (var i = 0; i < object.length; i++) {
                // Iterate through filter entries
                if (typeof object[i][1] !== 'object' && object[i][1] !== '') {
                    // Check if the value is not an object and not empty
                    if (object[i][0] == 'Datepicker') {
                        var date = object[i][1].split(' - '); // Split date range
                        string1 = string1 + '&dateFrom=' + date[0]; // Add start date to filter string
                        string1 = string1 + '&dateTo=' + date[1]; // Add end date to filter string
                    } else {
                        if (object[i][0] != 'Date') {
                            string1 = string1 + '&' + object[i][0] + '=' + object[i][1]; // Add other filters to string
                        } else {
                            if (object[i][0] == 'Date') {
                                var date = object[i][1].replace(' 00:00:00', ''); // Format date
                                string1 = string1 + '&dateFrom=' + date; // Add start date to filter string
                                string1 = string1 + '&dateTo=' + date; // Add end date to filter string
                            }
                        }
                    }
                } else {
                    if (typeof object[i][1] === 'object') {
                        // Check if the value is an object
                        var object2 = Object.entries(object[i][1]); // Convert object to entries
                        for (var j = 0; j < object2.length; j++) {
                            // Iterate through nested object entries
                            if (object2[j][1] !== null) {
                                if (object2[j][0] === 'start') {
                                    var d = new Date(object2[j][1]); // Create date object for start date
                                    var dateFrom =
                                        'dateFrom=' +
                                        d.getFullYear() +
                                        ':' +
                                        ('00' + (d.getMonth() + 1)).slice(-2) +
                                        ':' +
                                        ('00' + d.getDate()).slice(-2) +
                                        ' ' +
                                        ('00' + d.getHours()).slice(-2) +
                                        ':' +
                                        ('00' + d.getMinutes()).slice(-2) +
                                        ':' +
                                        ('00' + d.getSeconds()).slice(-2);
                                    string1 = string1 + '&' + dateFrom; // Add start date to filter string
                                } else {
                                    if (object2[j][0] === 'end') {
                                        var d = new Date(object2[j][1]); // Create date object for end date
                                        var dateTo =
                                            'dateTo=' +
                                            d.getFullYear() +
                                            ':' +
                                            ('00' + (d.getMonth() + 1)).slice(-2) +
                                            ':' +
                                            ('00' + d.getDate()).slice(-2) +
                                            ' ' +
                                            ('00' + d.getHours()).slice(-2) +
                                            ':' +
                                            ('00' + d.getMinutes()).slice(-2) +
                                            ':' +
                                            ('00' + d.getSeconds()).slice(-2);
                                        string1 = string1 + '&' + dateTo; // Add end date to filter string
                                    }
                                }
                            }
                        }
                    }
                }
            }
            grid.clearAll(); // Clear existing data in the grid
            grid.load(url + '?start=0&count=' + grid.data.$pagesize + string1); // Load data with filters
            filter = string1; // Update filter variable
        });

        grid.attachEvent('onAfterSort', function (by, dir, as) {
            // Event after sorting
            grid.load(
                url + '?start=0&count=' + grid.data.$pagesize + filter + '&sort[' + by + ']=' + dir, // Load data with sorting
            );
        });

        grid.attachEvent('onItemClick', function (id, e, node) {
            // Event on item click
            var item = this.getItem(id); // Get clicked item
            this.$scope.app.callEvent('user:id', [item.Id]); // Trigger user ID event
        });

        this.on(this.app, 'player:select', (Id) => {
            // Event for player selection
            grid.load(
                grid.data.url + '?start=0&count=' + grid.data.$pagesize + '&PlayerId=' + Id, // Load data for selected player
            );
        });

        this.on(this.app, "export:filter", filterData => { // Event for exporting data
            flag = true; // Set export flag
            if (flag) { // Check if export flag is set
                var size = grid.getPager().config.size; // Get pager size
                var page = grid.getPage(); // Get current page
                var first = size * page; // First item index on current page
                var last = (first + size) - 1; // Last item index on current page
                if ($$("Export").getValue() == '1') { // Check for Excel export
                    for (var j = 0; j < grid.config.columns.length; j++) {
                        if (grid.config.columns[j].exportType) {
                            delete grid.config.columns[j].format; // Remove format for export
                        }
                    }
                    this.app.callEvent('export:data', [
                        // Trigger export event
                        {
                            NameReport:
                                ($$('datatable').count() > 0 ? '' : _('No hay datos - ')) +
                                _('Historial de saldo'), // Report name
                            TypeReport: 'Excel', // Report type
                        },
                    ]);
                    webix.toExcel(grid, {
                        filter: function (obj) {
                            return grid.getIndexById(obj.id) >= first && grid.getIndexById(obj.id) <= last; // Filter for current page
                        },
                        // Export to Excel
                        filename:
                            'Virtualsoft - ' +
                            new Date()
                                .toLocaleString('en-GB', {
                                    hour12: true,
                                })
                                .replace(/^(\d{4})-(\d{2})-(\d{2})$/g, '$3/$2/$1')
                                .replace(/[\/:]/g, '_')
                                .toUpperCase() +
                            ' - ' +
                            ($$('datatable').count() > 0 ? '' : _('No hay datos - ')) +
                            _('Historial de saldo'), // Filename for the export
                        ignore: { detailTittle: true }, // Ignore detail title
                        footer: false, // Do not include footer
                    });
                } else {
                    if ($$("Export").getValue() == '2') { // Check for PDF export
                        this.app.callEvent('export:data', [
                            // Trigger export event
                            {
                                NameReport:
                                    ($$('datatable').count() > 0 ? '' : _('No hay datos - ')) +
                                    _('Historial de saldo'), // Report name
                                TypeReport: 'PDF', // Report type
                            },
                        ]);
                        webix.toPDF(grid, {
                            filter: function (obj) {
                                return grid.getIndexById(obj.id) >= first && grid.getIndexById(obj.id) <= last; // Filter for current page
                            },
                            // Export to PDF
                            filename:
                                'Virtualsoft - ' +
                                new Date()
                                    .toLocaleString('en-GB', {
                                        hour12: true,
                                    })
                                    .replace(/^(\d{4})-(\d{2})-(\d{2})$/g, '$3/$2/$1')
                                    .replace(/[\/:]/g, '_')
                                    .toUpperCase() +
                                ' - ' +
                                ($$('datatable').count() > 0 ? '' : _('No hay datos - ')) +
                                _('Historial de saldo'), // Filename for the export
                            autowidth: true, // Auto width for PDF
                            ignore: { detailTittle: true }, // Ignore detail title
                            footer: false, // Do not include footer
                        });
                    }
                }
                flag = false; // Reset export flag
                $$("Export").setValue(""); // Reset export selection
            }
        });

    }
}

// FILE DOCUMENTED
